<script lang="ts">
	import { Modal, Button, Text } from '@Shelly/shelly-design-system';
	import { devices } from '../../../stores';
	import { flowEditorState } from '../../../stores/flow_editor';
	import {
		analyzeFlowRepairIssues,
		applyFlowRepair,
		type FlowRepairIssue,
	} from '../helpers/flowGraphRepair';

	export let showModal = false;
	export let isMobile = false;
	export let onClose: () => void;
	export let onFocusNode: (nodeId: string) => void;

	$: issues = analyzeFlowRepairIssues($flowEditorState.nodes, $flowEditorState.edges, $devices);

	function reconfigure(issue: FlowRepairIssue) {
		if (!issue.nodeId) return;
		onClose();
		onFocusNode(issue.nodeId);
	}

	function remove(issue: FlowRepairIssue, suggestion: 'remove-node' | 'remove-edge') {
		if (!window.confirm('Remove this broken Flow item? You can use Undo immediately afterward.')) {
			return;
		}
		const result = applyFlowRepair(
			$flowEditorState.nodes,
			$flowEditorState.edges,
			issue,
			suggestion,
		);
		flowEditorState.setNodesAndEdges(result.nodes, result.edges, { markDirty: true });
	}
</script>

<Modal
	{showModal}
	title="Repair Flow"
	type={isMobile ? 'fullScreen' : 'default'}
	backdropClose={false}
	on:close={onClose}
>
	<svelte:fragment slot="content">
		<div class="repair-content">
			{#if issues.length}
				<Text variant="secondary" size="m">
					Review each problem. Reconfiguration keeps the node and lets you choose replacement
					resources.
				</Text>
				<div class="repair-list">
					{#each issues as issue (issue.id)}
						<div class="repair-row">
							<div>
								<strong>{issue.kind.replace(/_/g, ' ')}</strong>
								<span>{issue.message}</span>
							</div>
							<div class="repair-actions">
								{#if issue.nodeId}
									<Button variant="secondary" on:click={() => reconfigure(issue)}>
										Reconfigure
									</Button>
								{/if}
								{#if issue.edgeId}
									<Button
										variant="danger"
										on:click={() => remove(issue, 'remove-edge')}
									>
										Remove connection
									</Button>
								{:else if issue.nodeId}
									<Button
										variant="danger"
										on:click={() => remove(issue, 'remove-node')}
									>
										Remove node
									</Button>
								{/if}
							</div>
						</div>
					{/each}
				</div>
			{:else}
				<div class="all-clear">
					<i class="fas fa-circle-check" aria-hidden="true"></i>
					<Text variant="body" size="m">No repairable Flow problems were found.</Text>
				</div>
			{/if}
		</div>
	</svelte:fragment>
	<div slot="footer" class="footer">
		<Button variant="primary" on:click={onClose}>Done</Button>
	</div>
</Modal>

<style>
	.repair-content,
	.repair-list {
		display: grid;
		gap: 12px;
	}
	.repair-content {
		box-sizing: border-box;
		width: 100%;
		max-width: 100%;
	}
	.repair-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 16px;
		padding: 12px;
		border: 1px solid var(--color-3, rgba(148, 163, 184, 0.28));
		border-radius: 8px;
	}
	.repair-row > div:first-child {
		display: grid;
		gap: 4px;
	}
	.repair-row strong {
		text-transform: capitalize;
	}
	.repair-row span {
		color: var(--color-text-secondary, #64748b);
		font-size: 12px;
	}
	.repair-actions,
	.footer,
	.all-clear {
		display: flex;
		align-items: center;
		gap: 8px;
	}
	.footer {
		justify-content: flex-end;
	}
	.all-clear {
		justify-content: center;
		padding: 24px;
		color: var(--color-success, #16a34a);
	}
	@media (max-width: 639px) {
		.repair-row {
			align-items: stretch;
			flex-direction: column;
		}
		.repair-actions {
			flex-wrap: wrap;
		}
		.repair-actions > :global(*) {
			flex: 1;
		}
	}
</style>
